import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  BookOpen,
  MessageSquare,
  Scale,
  GraduationCap,
  FileText,
  Search,
  Sun,
  Moon,
  Menu,
  Home,
  HardDrive,
  Globe,
  Network
} from 'lucide-react';
import { getLang, setLang } from '../../lib/i18n';
import { useTranslation } from '../../hooks/useTranslation';

const navItems = [
  { path: '/', labelKey: 'nav.home', icon: Home },
  { path: '/library', labelKey: 'nav.library', icon: BookOpen },
  { path: '/chat', labelKey: 'nav.ask', icon: MessageSquare },
  { path: '/case', labelKey: 'nav.caseTool', icon: Scale },
  { path: '/learn', labelKey: 'nav.learn', icon: GraduationCap },
  { path: '/simulate', labelKey: 'nav.simulator', icon: FileText },
  { path: '/compare', labelKey: 'nav.compareTool', icon: Globe },
  { path: '/graph', labelKey: 'nav.graphTool', icon: Network },
  { path: '/offline', labelKey: 'nav.offline', icon: HardDrive },
];

export function Navigation() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentLang = getLang();
  const { t } = useTranslation();

  const handleLangChange = (lang) => {
    setLang(lang);
    window.location.reload();
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
            <Scale className="h-6 w-6 text-accent" />
            <span className="font-semibold text-lg hidden sm:inline" style={{ fontFamily: 'var(--font-heading)' }}>
              Canon Compass
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                data-testid={`nav-${t(item.labelKey).toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="h-4 w-4" />
                <span>{t(item.labelKey)}</span>
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 mr-2">
              <span className="text-xs text-muted-foreground font-medium hidden sm:inline-block">
                {t('nav.language')}: {currentLang.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center bg-muted/50 rounded-md p-0.5">
              <button
                onClick={() => handleLangChange('en')}
                className={`px-2 py-1 text-xs font-bold rounded-sm transition-all ${currentLang === 'en'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLangChange('el')}
                className={`px-2 py-1 text-xs font-bold rounded-sm transition-all ${currentLang === 'el'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
              >
                EL
              </button>
            </div>

            <Link to="/search" data-testid="nav-search">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Search className="h-4 w-4" />
              </Button>
            </Link>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <nav className="flex flex-col space-y-1 mt-8" data-testid="mobile-nav">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      data-testid={`mobile-nav-${t(item.labelKey).toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
